
import { useState } from "react";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../context/ThemeProvider';
import { toast } from 'react-toastify';
import { toastStyles } from '../styles/styles';

const useDelete = (
    queryKey,
    apiCall,
    deleteConfirmationMessage = 'Are you sure you want to delete this item? This action will remove the item from the system.',
    defaultSuccessMessage = 'Item deleted successfully',
    defaultErrorMessage = 'Item deletion failed'
) => {
    const queryClient = useQueryClient();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const { theme } = useTheme();

    const mutation = useMutation({
        mutationFn: async (item) => {
            const response = await apiCall(item);
            return response.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries([queryKey]);
            setIsDialogOpen(false);
            setSelectedItem(null);
            toast.success(data?.message || defaultSuccessMessage, {
                ...toastStyles,
                theme: theme
            });
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || defaultErrorMessage, {
                ...toastStyles,
                theme: theme
            });
            setIsDialogOpen(false);
            setSelectedItem(null);
        }
    });

    const handleDelete = (item) => {
        setSelectedItem(item);
        setIsDialogOpen(true);
    };

    const handleConfirm = () => {
        if (selectedItem) {
            mutation.mutate(selectedItem);
        }
    };


    return {
        deleteItem: handleDelete,
        isLoading: mutation.isPending,
        isError: mutation.isError,
        error: mutation.error,
        isSuccess: mutation.isSuccess,
        reset: mutation.reset,
        deleteDialogProps: {
            isOpen: isDialogOpen,
            onClose: () => setIsDialogOpen(false),
            onConfirm: handleConfirm,
            type: 'DELETE',
            confirmLabel: 'Delete',
            message: deleteConfirmationMessage,
            cancelLabel: 'Cancel'
        }
    };
};

export default useDelete;
